import '.ft{padding:8px 8px 4px 8px;}.ssr-plugin'
import '.fu{min-height:28px;}.ssr-plugin'
import '.de{outline:none;}.ssr-plugin'
import '.d7{overflow:auto;}.ssr-plugin'
import '.a0{position:relative;}.ssr-plugin'
import '.a1{height:100%;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.d8{min-width:240px;}.ssr-plugin'
import '.e2{padding:8px;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.do{gap:2px;}.ssr-plugin'
import '.da{flex-shrink:0;}.ssr-plugin'
import '.fv{min-height:32px;}.ssr-plugin'
import '.b{align-items:center;}.ssr-plugin'
import '.bl{gap:8px;}.ssr-plugin'
import '.fw{padding:4px 8px 4px 8px;}.ssr-plugin'
import '.p{border:none;}.ssr-plugin'
import '.b4{appearance:none;}.ssr-plugin'
import '.en{gap:0px;}.ssr-plugin'
import '.fx{white-space:nowrap;}.ssr-plugin'
import '.o{background:transparent;}.ssr-plugin'
import '.fq{padding:0 8px;}.ssr-plugin'
import '.q{cursor:pointer;}.ssr-plugin'
import '.dn{height:24px;}.ssr-plugin'
import '.fy{width:24px;}.ssr-plugin'
import '.ey{margin-left:auto;}.ssr-plugin'
import React, { createElement, useEffect, useRef, useState } from 'react'
import { Overlay } from '../Overlay/index.js'
import { OverlayTrigger } from '../Overlay/OverlayTrigger.js'
import { styled, Style } from 'inlines'
import { radius } from '../../utils/radius.js'
import { shadows } from '../../utils/shadows.js'
import { Separator } from '../Separator/index.js'
import { Icon, IconProps } from '../Icon/index.js'
import { Text } from '../Text/index.js'
import { KeyHint, KeyHintProps } from '../KeyHint/index.js'
import {
  OptionCardGroup,
  OptionCardGroupProps,
} from '../OptionCardGroup/index.js'
import { colors } from '../../tokens/colors.js'
import { TextInput } from '../TextInput/index.js'
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut.js'

export type MenuProps = {
  children: React.ReactNode
  position?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'bottom-center'
    | 'top-center'
  onClose?: () => void
  onOpen?: () => void
  openOnHover?: boolean
  scrollableParent?: boolean
  autoScroll?: boolean
  style?: Style
}

const Menu = ({
  children,
  position = 'bottom',
  onClose,
  onOpen,
  openOnHover,
  scrollableParent = true,
  autoScroll = false,
}: MenuProps) => {
  const triggerElement = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === OverlayTrigger,
  )

  const menuItems = React.Children.toArray(children).filter(
    (child) => !React.isValidElement(child) || child.type === MenuItems,
  )

  // Pass autoScroll to MenuItems
  const menuItemsWithAutoScroll = React.Children.map(menuItems, (child) => {
    if (React.isValidElement(child) && child.type === MenuItems) {
      return React.cloneElement(child as React.ReactElement<any>, {
        autoScroll,
      })
    }
    return child
  })

  return (
    <Overlay
      position={position}
      scrollableParent={scrollableParent}
      openOnHover={openOnHover}
      onClose={onClose}
      onOpen={onOpen}
    >
      <Overlay.Trigger>{triggerElement}</Overlay.Trigger>
      <Overlay.Content>{menuItemsWithAutoScroll}</Overlay.Content>
    </Overlay>
  )
}

const MenuSeparator = () => {
  return (
    <Separator
      orientation="horizontal"
      style={{ padding: '4px 8px 4px 8px', height: 1 }}
    />
  )
}

type MenuHeaderProps = {
  label?: string
  children?: React.ReactNode
  style?: Style
}

const MenuHeader = ({ label, children, style }: MenuHeaderProps) => {
  return (
    <styled.div className="ft fu" style={{ ...style }}>
      <Text color="neutral60" variant="subtext-medium">
        {children || label}
      </Text>
    </styled.div>
  )
}

type MenuItemsProps = {
  children: React.ReactNode
  style?: Style
  filterable?: boolean
  autoScroll?: boolean
}

const MenuItems: React.FC<MenuItemsProps> = ({
  children,
  style,
  filterable = false,
  autoScroll = false,
}) => {
  const [filter, setFilter] = useState<string>()
  const [childrenCounter, setChildrenCounter] = useState(
    React.Children.count(children),
  )
  const [childrenNavigation, setChildrenNavigation] = useState([])
  const childrenArr = []
  const inputRef = useRef<HTMLInputElement>()
  const [navigation, setNavigation] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map())

  const shouldBeFilterable =
    childrenCounter > 5 ? filterable !== false : filterable

  React.Children.forEach(children, (child) => {
    childrenArr.push(child)
  })

  const toText = (v: any) => {
    if (v == null) return ''
    if (typeof v === 'string') return v
    if (Array.isArray(v)) return v.map(toText).join(' ')
    if (typeof v === 'number' || typeof v === 'boolean') return String(v)
    return ''
  }

  const filteredItems: React.ReactNode[] = React.Children.toArray(
    childrenArr,
  ).reduce<React.ReactNode[]>((acc, node) => {
    const process = (child: any) => {
      if (!child) return

      if (child.type === React.Fragment) {
        React.Children.forEach(child.props?.children, (sub) => process(sub))
        return
      }

      const rawLabel =
        (child as any).label ?? child.props?.label ?? child.props?.children

      if (filter) {
        const labelText = toText(rawLabel).toLowerCase()
        if (!labelText.includes(filter.toLowerCase())) return
      }

      const index = acc.length

      if (child.type === Menu.Item) {
        acc.push(
          <div
            key={index}
            ref={(el: HTMLDivElement | null) => {
              if (el) {
                itemRefs.current.set(index, el)
              } else {
                itemRefs.current.delete(index)
              }
            }}
          >
            {React.createElement(child.type, {
              ...child.props,
              hover: childrenNavigation[navigation] === index,
            })}
          </div>,
        )
      } else {
        acc.push(
          React.createElement(child.type, {
            ...child.props,
            key: index,
          }),
        )
      }
    }

    process(node)
    return acc
  }, [])

  useEffect(() => {
    const childrenMapping = filteredItems.reduce(
      (acc: Record<string, any>, child: any, index: number) => {
        if (child.type === Menu.Item && !child.props?.disabled) {
          acc = {
            ...acc,
            counter: acc.counter + 1,
            navigation: [...acc.navigation, index],
          }
        }
        return acc
      },
      {
        counter: 0,
        navigation: [],
      },
    )

    setChildrenNavigation(childrenMapping.navigation)
  }, [filteredItems.length])

  useEffect(() => {
    if (!autoScroll || !containerRef.current) return

    const timer = setTimeout(() => {
      let firstSelectedIndex = -1
      React.Children.forEach(childrenArr, (child: any, idx) => {
        if (firstSelectedIndex === -1 && child?.props?.selected === true) {
          firstSelectedIndex = idx
        }
      })

      if (firstSelectedIndex !== -1) {
        const selectedElement = itemRefs.current.get(firstSelectedIndex)
        if (selectedElement && containerRef.current) {
          selectedElement.scrollIntoView({
            behavior: 'auto',
            block: 'start',
          })
          containerRef.current.scrollTop -= 8
        }
      }
    }, 0)

    return () => clearTimeout(timer)
  }, [autoScroll, children])

  useEffect(() => {
    if (shouldBeFilterable) {
      inputRef.current?.focus()
    }
  })

  useKeyboardShortcut(
    'ArrowUp',
    () =>
      setNavigation((prev) => {
        if (prev > 0) {
          return prev - 1
        } else if (prev === 0) {
          return childrenNavigation.length - 1
        }
        return prev
      }),
    inputRef.current,
  )

  useKeyboardShortcut(
    'ArrowDown',
    () =>
      setNavigation((prev) => {
        if (prev < childrenNavigation.length - 1) {
          return prev + 1
        } else if (prev === childrenNavigation.length - 1) {
          return 0
        }
        return prev
      }),
    inputRef.current,
  )

  useKeyboardShortcut(
    'Enter',
    () => {
      if (navigation !== undefined) {
        childrenArr[childrenNavigation[navigation]]?.props?.onClick?.()
      }
    },
    inputRef.current,
  )

  useEffect(() => {
    if (navigation !== undefined) {
      setNavigation(0)
    }
  }, [filter, shouldBeFilterable])

  return (
    <styled.div className="menu-items de d7 a0 a1 h d8 e2 a i do"
      ref={containerRef}
      onMouseEnter={() => setNavigation(undefined)}
      onMouseLeave={() => {
        setNavigation(undefined)
      }}
      style={{
        borderRadius: radius[20],
        background: `linear-gradient(${colors.neutral10Background}),
          linear-gradient(${colors.neutralInverted100})`,
        color: colors.neutral80,
        border: `1px ${colors.neutral10} solid`,
        boxShadow: `${shadows.popoverLarge}`,
        ...style,
      }}
    >
      {shouldBeFilterable && (
        <>
          <TextInput
            ref={inputRef}
            value={filter}
            onChange={(value) => {
              setFilter(value)
            }}
            variant="ghost"
            leadIcon="search"
            placeholder="Find..."
          />
          {filteredItems.length > 0 && <Menu.Separator />}
        </>
      )}
      {childrenCounter ? filteredItems : null}
    </styled.div>
  )
}

export type MenuItemProps = {
  children?: React.ReactNode
  label?: string
  description?: string
  leadIcon?: IconProps['variant']
  trailIcon?: IconProps['variant']
  color?: 'neutral' | 'red'
  indentContent?: boolean
  visualOnlyKeyHint?: KeyHintProps['hint']
  disabled?: boolean
  onClick?: () => void
  keepOpenOnClick?: boolean
  selected?: boolean
  hover?: boolean
  style?: Style
}

const MenuItem = ({
  children,
  label,
  description,
  leadIcon,
  trailIcon,
  selected = false,
  color = 'neutral',
  indentContent = false,
  visualOnlyKeyHint,
  disabled,
  onClick,
  keepOpenOnClick = false,
  hover: hoverProp,
  style,
}: MenuItemProps) => {
  const [hover, setHover] = useState(hoverProp)

  useEffect(() => {
    setHover(hoverProp)
  }, [hoverProp])

  return (
    <styled.button className="da fv h a b bl fw p b4 de"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false)
      }}
      tabIndex={-1}
      type="button"
      disabled={disabled}
      onClick={() => {
        if (disabled) return
        onClick?.()
        if (!keepOpenOnClick) {
          Overlay.CloseAll()
        }
      }}
      style={{
        height: description ? 'auto' : children ? '100%' : undefined,
        color: colors.neutral80,
        opacity: disabled ? 0.2 : undefined,
        cursor: !disabled && onClick ? 'pointer' : undefined,
        borderRadius: radius[12],
        backgroundColor:
          hover && !disabled && onClick
            ? color === 'red'
              ? colors.red10
              : colors.neutral10Adjusted
            : 'transparent',
        ...style,
      }}
    >
      {indentContent && <span style={{ height: 24, width: 24 }} />}
      {selected && (
        <Icon
          variant="checkmark-normal"
          color={color === 'red' ? 'red80' : 'neutral80'}
        />
      )}
      {leadIcon && !selected && (
        <Icon
          variant={leadIcon}
          color={color === 'red' ? 'red80' : 'neutral80'}
        />
      )}
      {typeof children === 'string' ? (
        <Text
          variant="display-medium"
          color={color === 'red' ? 'red80' : 'neutral80'}
          singleLine
        >
          {children}
        </Text>
      ) : (
        <>{children}</>
      )}
      {(label || description) && !children && (
        <div className="a i en"
        >
          {label && (
            <Text
              variant="display-medium"
              color={color === 'red' ? 'red80' : 'neutral80'}
              style={{ width: '100%' }}
            >
              {label}
            </Text>
          )}
          {description && (
            <Text
              variant="subtext-regular"
              color={color === 'red' ? 'red60' : 'neutral60'}
              style={{ width: '100%' }}
            >
              {description}
            </Text>
          )}
        </div>
      )}
      {visualOnlyKeyHint && <KeyHint hint={visualOnlyKeyHint} color={color} />}
      {trailIcon && <Icon variant={trailIcon} style={{ marginLeft: 'auto' }} />}
    </styled.button>
  )
}

type MenuToggleItemProps = {
  children?: string | React.ReactNode
  label?: string
  description?: string
  toggled?: boolean
  onChange?: (value: boolean) => void
  keepOpenOnClick?: boolean
  disabled?: boolean
  visualOnlyKeyHint?: KeyHintProps['hint']
  color?: 'neutral' | 'red'
  value?: string | number | boolean
}

const MenuToggleItem = ({
  children,
  toggled = false,
  onChange,
  keepOpenOnClick = true,
  label,
  description,
  disabled,
  visualOnlyKeyHint,
  color = 'neutral',
  value,
}: MenuToggleItemProps) => {
  return (
    <styled.button className="da fx fv h o a b bl fq q p b4 de"
      tabIndex={-1}
      onClick={() => {
        if (disabled) return
        else if (!keepOpenOnClick) {
          onChange?.(toggled)
          Overlay.CloseAll()
        } else {
          onChange?.(toggled)
        }
      }}
      type="button"
      disabled={disabled}
      style={{
        height: description ? 54 : children ? '100%' : undefined,
        color: colors.neutral80,
        opacity: disabled ? 0.2 : undefined,
        borderRadius: radius[12],
        '&:hover': {
          background: color === 'red' ? colors.red10 : colors.neutral10Adjusted,
        },
      }}
    >
      <div className="dn fy">
        {toggled && (
          <Icon
            variant="checkmark-normal"
            color={color === 'red' ? 'red80' : 'neutral80'}
          />
        )}
      </div>
      {typeof children === 'string' ? (
        <Text
          variant="display-medium"
          color={color === 'red' ? 'red80' : 'neutral80'}
          singleLine
        >
          {children}
        </Text>
      ) : (
        children
      )}
      {label && (
        <div className="a i en"
        >
          <Text
            variant="display-medium"
            color={color === 'red' ? 'red80' : 'neutral80'}
            style={{ width: '100%' }}
          >
            {children || label}
          </Text>
          {description && (
            <Text
              variant="subtext-regular"
              color={color === 'red' ? 'red60' : 'neutral60'}
              style={{ width: '100%' }}
            >
              {description}
            </Text>
          )}
        </div>
      )}
      {visualOnlyKeyHint && (
        <div className="ey">
          <KeyHint hint={visualOnlyKeyHint} />
        </div>
      )}
    </styled.button>
  )
}

type MenuOptionCardGroupProps = OptionCardGroupProps

const MenuOptionCardGroup = (props: MenuOptionCardGroupProps) => {
  return <OptionCardGroup {...props} />
}

Menu.Trigger = OverlayTrigger
Menu.Items = MenuItems
Menu.Header = MenuHeader
Menu.Item = MenuItem
Menu.Separator = MenuSeparator
Menu.OptionCardGroup = MenuOptionCardGroup
Menu.ToggleItem = MenuToggleItem
Menu.Menu = Menu
Menu.Close = Overlay.Close
Menu.CloseAll = Overlay.CloseAll

export { Menu }
